import React from "react";
import {
  blockHeroTitleClass,
  blockProjectTitleClass,
  blockSectionTitleClass,
  blockMonoLabelClass,
  blockTechnicalLabelClass,
  blockLedeClass,
  blockSectionSpacingClass,
  blockMetaLineClass,
} from "./layoutTokens";

/** Reference strip — meta row above hero */
export function PageMeta({ left = [], right = [] }) {
  return (
    <div
      className={`flex flex-wrap justify-between gap-x-6 gap-y-2 pb-3 mb-1 ${blockMetaLineClass}`}
    >
      <div className="flex flex-wrap gap-x-[18px] gap-y-1">
        {left.map((t, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-content-subtle dark:text-neutral-600 opacity-70">·</span>}
            <span>{t}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-[18px] gap-y-1">
        {right.map((t, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-content-subtle dark:text-neutral-600 opacity-70">·</span>}
            <span>{t}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export function BlockHero({ kicker, title, titleAccent, lede }) {
  const hasLede = lede != null && lede !== "";
  return (
    <header className="py-10 lg:py-12 lg:pb-8 max-w-[min(880px,100%)]">
      {kicker ? <p className={`${blockMonoLabelClass} mb-3`}>{kicker}</p> : null}
      <h1 className={`${blockHeroTitleClass} mb-4`}>
        {title}
        {titleAccent ? (
          <>
            {" "}
            <span className="text-content-secondary dark:text-neutral-400">{titleAccent}</span>
          </>
        ) : null}
      </h1>
      {hasLede && (
        <div className={`${blockLedeClass} space-y-4 [&_p]:m-0`}>
          {typeof lede === "string" ? <p>{lede}</p> : lede}
        </div>
      )}
    </header>
  );
}

/**
 * Compact case-study hero — three layers, stratified by size:
 *   1. title       (text-section — smaller than the big BlockHero)
 *   2. description (text-lede — the one-paragraph summary)
 *   3. meta        (mono caps — related words: company, tools, year, tags)
 *
 * Use when the project page needs the typographic rhythm of a hero
 * without the cinematic weight of `text-hero`.
 */
export function BlockHeroCompact({ kicker, title, description, meta = [] }) {
  const metaList = Array.isArray(meta) ? meta : String(meta).split("·").map((s) => s.trim()).filter(Boolean);
  return (
    <header className="pt-10 lg:pt-14 pb-6 lg:pb-8 max-w-[min(880px,100%)]">
      {kicker ? <p className={`${blockMonoLabelClass} mb-3`}>{kicker}</p> : null}
      <h1 className={`${blockProjectTitleClass} mb-4`}>{title}</h1>
      {description ? (
        <p className={`${blockLedeClass} mb-5`}>{description}</p>
      ) : null}
      {metaList.length > 0 ? (
        <ul className={`${blockTechnicalLabelClass} flex flex-wrap gap-x-3 gap-y-1 list-none p-0 m-0`}>
          {metaList.map((m, i) => (
            <React.Fragment key={`${m}-${i}`}>
              {i > 0 && <li aria-hidden className="text-content-subtle dark:text-neutral-600 opacity-70">·</li>}
              <li>{m}</li>
            </React.Fragment>
          ))}
        </ul>
      ) : null}
    </header>
  );
}

/**
 * RecipeBoard — single hairline wrapper around a stack of RecipeRows.
 * Hoisted from SILTMiddelkerke.js so every project page shares one definition.
 */
export function RecipeBoard({ children, className = "" }) {
  return (
    <div
      className={`border border-border dark:border-neutral-800 bg-white dark:bg-black overflow-hidden ${className}`}
    >
      {children}
    </div>
  );
}

export function BlockSection({ answerLabel, title, titleAccent, children, className = "" }) {
  return (
    <section className={`${blockSectionSpacingClass} ${className}`}>
      <h2 className={`${blockSectionTitleClass} mb-5`}>
        {answerLabel && <span className={`block ${blockMonoLabelClass} mb-1.5`}>{answerLabel}</span>}
        {title}
        {titleAccent ? (
          <>
            {" "}
            <span className="text-content-secondary dark:text-neutral-400">{titleAccent}</span>
          </>
        ) : null}
      </h2>
      {children}
    </section>
  );
}
